import { useEffect, useRef, useState } from "react";
import type { TransitionEvent } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@shadcn/components/ui/button";
import { cn } from "@shadcn/lib/utils";

export type TurnstileActionFeedbackState = "pending" | "success" | "error";

export type TurnstileActionFeedbackProps = {
  state: TurnstileActionFeedbackState;
  onRetry?: () => void;
  className?: string;
};

const successVisibleMs = 2800;
const successFadeMs = 500;

function TurnstileSuccessMessage({ className }: { className?: string }) {
  const [fade, setFade] = useState(false);
  const [gone, setGone] = useState(false);
  const fadeFallbackRef = useRef<number | null>(null);

  useEffect(() => {
    const startFade = window.setTimeout(() => setFade(true), successVisibleMs);
    return () => window.clearTimeout(startFade);
  }, []);

  useEffect(() => {
    if (!fade) return;
    fadeFallbackRef.current = window.setTimeout(() => setGone(true), successFadeMs + 80);
    return () => {
      if (fadeFallbackRef.current !== null) window.clearTimeout(fadeFallbackRef.current);
    };
  }, [fade]);

  if (gone) return null;

  const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || e.propertyName !== "opacity") return;
    if (fadeFallbackRef.current !== null) {
      window.clearTimeout(fadeFallbackRef.current);
      fadeFallbackRef.current = null;
    }
    setGone(true);
  };

  return (
    <div
      className={cn(
        "text-center text-sm text-muted-foreground transition-opacity ease-out",
        fade ? "opacity-0" : "opacity-100",
        className
      )}
      style={{ transitionDuration: `${successFadeMs}ms` }}
      role="status"
      aria-live="polite"
      onTransitionEnd={handleTransitionEnd}
    >
      <span>You seem to be a human, you can carry on!</span>
    </div>
  );
}

export function TurnstileActionFeedback({ state, onRetry, className }: TurnstileActionFeedbackProps) {
  if (state === "pending") {
    return (
      <div
        className={cn(
          "flex items-center justify-center gap-2 text-center text-sm text-muted-foreground",
          className
        )}
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
        <span>Please wait while we verify you&apos;re a human being!</span>
      </div>
    );
  }

  if (state === "success") {
    return <TurnstileSuccessMessage className={className} />;
  }

  return (
    <div className={cn("flex flex-col items-center gap-3 text-center", className)}>
      <p className="text-sm text-destructive" role="alert">
        We couldn&apos;t verify the security check. Please try again, or refresh the page if this keeps happening.
      </p>
      {onRetry ? (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}
